import { useRef, useEffect } from 'react';
import { Image, Text } from '@ui/atoms';
import { useRouter } from 'next/router';
import { useWindowSize } from '@uidotdev/usehooks';
import { triggerCustomGaEvent } from '@helpers/miscelleanous';
import { bondAnalyserGaEventsName } from '@helpers/ga-events-name-enum';

const HeroBanner = () => {
    const svgRef = useRef(null);
    const buttonRef = useRef(null);
    const router = useRouter();
    const { width } = useWindowSize();

    const mousePos = useRef({ x: 0, y: 0 });
    const animationFrame = useRef(null);

    // Mouse move event handler to capture mouse position relative to the SVG
    const handleMouseMove = (e) => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;

        mousePos.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };

        // Move the SVG element based on the mouse position
        if (svgRef.current) {
            svgRef.current.style.transform = `translate(${mousePos.current.x}px, ${mousePos.current.y}px)`;
        }
    };

    // Function to animate the glow effect (optional, can be removed if you don't need the glow effect)
    const animate = () => {
        if (!svgRef.current) return;

        // Directly move the SVG element to follow the mouse position
        svgRef.current.style.transform = `translate(${mousePos.current.x}px, ${mousePos.current.y}px)`;

        animationFrame.current = requestAnimationFrame(animate);
    };

    const startAnimation = () => {
        if (!animationFrame.current) {
            animationFrame.current = requestAnimationFrame(animate);
        }
    };

    const stopAnimation = () => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
            animationFrame.current = null;
        }
    };

    useEffect(() => {
        return () => stopAnimation(); // Cleanup on unmount
    }, []);

    return (
        <div className="bg-bond-analyser-home-page-hero-banner max-h-[1000px] h-[calc(102svh)] xl:h-[calc(120svh)] relative overflow-hidden flex items-center flex-col">
            <div className="mx-auto flex flex-col xl:pt-48 w-full h-full bg-bond-analyser-hero-banner-secondary-bg-mobile xl:bg-bond-analyser-hero-banner-secondary-bg bg-no-repeat xl:!bg-[length:100%_100%]" style={{
                backgroundSize: width < 1280 ? "130%" : "254%",
                backgroundPositionX: "50%"
            }}>
                <div className='flex flex-col h-[100svh] xl:h-auto justify-center'>
                    <Text
                        content="Unlock the power of X-ray vision with"
                        className="h6-medium xl:h2-regular text-basicWhite text-center"
                    />
                    <Text
                        content="Jiraaf Bond Analyser"
                        className="h3-semibold xl:d1-semibold text-bond-analyser-homepage-herobanner-heading text-center w-max mx-auto mt-2 xl:mt-0"
                        style={{
                            textShadow: "0px 0px 8px rgba(253, 209, 127, 0.50)"
                        }}
                    />
                    <Image
                        src="/images/bond-analyser/hero-banner-triangle.svg"
                        className="w-6 h-6 mx-auto my-6"
                    />
                    <Text
                        content="See through the market noise and make investment decisions backed by data, not guesswork."
                        className="p5-regular px-4 xl:p2-regular text-basicWhite text-center"
                    />

                    <button
                        ref={buttonRef}
                        className="relative px-5 py-3 bg-basicWhite border-2 border-secondary-300 shadow-bond-analyser-xs rounded-full mx-auto mt-14 xl:mt-12 text-primary-500 p4-medium overflow-hidden group"
                        onMouseMove={handleMouseMove}
                        onMouseEnter={startAnimation}
                        onMouseLeave={stopAnimation}
                        onClick={() => {
                            triggerCustomGaEvent(bondAnalyserGaEventsName.BA_CLICKED_EXPLORE_HEROSECTION);
                            router.push('/bond-analyser/categories')
                        }}
                    >
                        <span className="relative z-10">
                            Explore our Bond Analyser Suite
                        </span>
                        <span
                            ref={svgRef}
                            className="absolute inset-0 z-0 pointer-events-none transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                        >
                            
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="144"
                                height="44"
                                viewBox="0 0 144 44"
                                fill="none"
                            >
                                <g filter="url(#filter0_f_40990_273555)">
                                    <ellipse
                                        cx="72"
                                        cy="33"
                                        rx="44"
                                        ry="11"
                                        fill="url(#paint0_linear_40990_273555)"
                                    />
                                </g>
                                <defs>
                                    <filter
                                        id="filter0_f_40990_273555"
                                        x="0"
                                        y="-6"
                                        width="144"
                                        height="78"
                                        filterUnits="userSpaceOnUse"
                                        color-interpolation-filters="sRGB"
                                    >
                                        <feFlood
                                            flood-opacity="0"
                                            result="BackgroundImageFix"
                                        />
                                        <feBlend
                                            mode="normal"
                                            in="SourceGraphic"
                                            in2="BackgroundImageFix"
                                            result="shape"
                                        />
                                        <feGaussianBlur
                                            stdDeviation="14"
                                            result="effect1_foregroundBlur_40990_273555"
                                        />
                                    </filter>
                                    <linearGradient
                                        id="paint0_linear_40990_273555"
                                        x1="28.1588"
                                        y1="36.6667"
                                        x2="114.575"
                                        y2="36.1849"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#68C0BC" />
                                        <stop
                                            offset="0.333333"
                                            stop-color="#B8B8EA"
                                        />
                                        <stop offset="0.67" stop-color="#EAA9AC" />
                                        <stop offset="1" stop-color="#E7CF79" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </span>
                    </button>
                </div>
            </div>
            <WhiteBottomComponent size={width > 1280 ? 1440 : 5840} />
        </div>
    );
};

const WhiteBottomComponent = ({
    size
}) => (
    <div className="absolute -bottom-8 xl:bottom-0 left-0 right-0 w-full h-[100px] xl:h-[200px] overflow-hidden">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 1440 200"
            fill="none"
            preserveAspectRatio="none"
        >
            <circle cx="720" cy={size} r={size} fill="white" />
        </svg>
    </div>
);

export default HeroBanner;
